'use client';

import { Button } from '@av/ui/button';
import { Form } from '@av/ui/form';
import { formatter } from '@av/utils';
import { CartItemForm, CartItemFormSchema, GatewayProductForm } from '@av/validators';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTransition } from 'react';
import { useForm } from 'react-hook-form';
import { useTaxState } from '../../../../components/context/tax-state-context';
import { useAccessories } from '@/app/components/accessories/accessories-context';
import { Product } from '@av/database';
import ProductQuantityForm from '@/app/components/product-quantity-form';
import AddOrderItemListButton from '@/app/assortiment/[[...slug]]/components/add-order-item-list-button';

const ProductForm = ({ product }: { product: GatewayProductForm }) => {
  const [loading, startTransition] = useTransition();
  const { productAccessoriesHandler } = useAccessories();
  const { isTaxIncluded } = useTaxState();

  const form = useForm<CartItemForm>({
    resolver: zodResolver(CartItemFormSchema),
    defaultValues: {
      quantity: product.minQuantity || 1,
      productId: product.id,
    },
  });

  const onSubmit = async (data: CartItemForm) => {
    startTransition(async () => {
      await productAccessoriesHandler({
        product: product as unknown as Product,
        quantity: data.quantity,
      });
    });
  };

  const taxFactor = product.specifications?.tax_rate ? product.specifications.tax_rate / 100 : 0;
  const priceFactor = isTaxIncluded ? 1 + taxFactor : 1;
  const productPrice = ((product.specifications?.price || 0) * priceFactor) / 100;

  let productTaxRate: string | null = null;

  if (product.specifications?.tax_rate) {
    productTaxRate = isTaxIncluded
      ? `incl. ${product.specifications.tax_rate}% BTW`
      : `excl. ${product.specifications.tax_rate}% BTW`;
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="w-full">
        <div className="mb-7 flex flex-wrap justify-between">
          <div className="mr-2 flex items-center gap-2">
            <ProductQuantityForm loading={loading} {...product} />
            <AddOrderItemListButton className="" productId={product.id} />
          </div>

          <div className="text-right">
            <span className="text-dark block text-[28px] font-semibold leading-[40px] dark:text-white">
              {formatter.format(productPrice)}
            </span>
            <span className="dark:text-dark-6 text-base font-normal text-description">
              {productTaxRate}
            </span>
          </div>
        </div>

        <Button className="mb-4 w-full md:mb-8">Toevoegen aan winkelwagen</Button>
      </form>
    </Form>
  );
};

export default ProductForm;
